import { useGetBonus } from 'react-easyrocket';
import { ModalWrapper } from '../../ModalWrapper';
import { useTranslation } from 'react-i18next';
import { CmsContent } from '../../CmsContent';
import { Button } from 'src/components/Buttons/Button';
import { useNavigate } from 'react-router';
import { useRoute } from 'src/router/hooks';
import { useModalContext } from 'src/hooks/useModalContext';
import { Skeletons } from 'src/components/Skeletons';
import { addNewDateToShownDatesLS } from 'src/config/helpers/eventModalsLS';
import { isProductionEnv } from 'src/api';

const BONUS_ID_BY_DAY_DEV: Record<number, string> = {
  7: '320',
  10: '310',
  13: '312',
  16: '311'
};

const BONUS_ID_BY_DAY_PROD: Record<number, string> = {
  7: '625',
  10: '626',
  13: '628',
  16: '627'
};

const bonusesIdsUse = isProductionEnv ? BONUS_ID_BY_DAY_PROD : BONUS_ID_BY_DAY_DEV;

const BONUS_TO_NAVIGATE_TO_DEV: Record<number, string | undefined> = {
  7: undefined,
  10: '303',
  13: '308',
  16: '307'
};

const BONUS_TO_NAVIGATE_TO_PROD: Record<number, string | undefined> = {
  7: undefined,
  10: '631',
  13: '636',
  16: '635'
};

const bonusesIdsNavigate = isProductionEnv ? BONUS_TO_NAVIGATE_TO_PROD : BONUS_TO_NAVIGATE_TO_DEV;

const PRIZE_AMOUNT_BY_DAY: Record<number, number | undefined> = {
  7: 80,
  10: undefined,
  13: 100,
  16: undefined
};

const ACTION_BY_DATE: Record<number, 'shop' | 'promo'> = {
  7: 'shop',
  10: 'promo',
  13: 'promo',
  16: 'promo'
};

interface ModalProps {
  day: number;
  fullDate: string;
}

export const EventModal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ROUTE } = useRoute();
  const { closeModal, modalProps } = useModalContext<ModalProps>();

  const bonusToUseId = bonusesIdsUse[modalProps?.day || 0];
  const { data, isLoading } = useGetBonus(bonusToUseId, { enabled: Boolean(bonusToUseId) });

  const updateLocalStorage = (fullDate: string | undefined) => {
    if (fullDate) {
      addNewDateToShownDatesLS(fullDate);
    }
  };

  const handleButtonClick = async () => {
    if (ACTION_BY_DATE[modalProps.day] === 'shop') {
      navigate(ROUTE.SHOP.get());
    } else {
      const bonusToNavigateTo = bonusesIdsNavigate[modalProps.day];
      if (bonusToNavigateTo) {
        navigate(ROUTE.BONUS.get(bonusToNavigateTo));
      }
    }
    updateLocalStorage(modalProps.fullDate);
    closeModal();
  };

  const handleClose = () => {
    updateLocalStorage(modalProps?.fullDate);
  };

  if (!data && !isLoading) {
    closeModal();
  }

  const prizeAmountToDisplay = PRIZE_AMOUNT_BY_DAY[modalProps?.day];

  const buttonText =
    ACTION_BY_DATE[modalProps.day] === 'shop'
      ? t('menuItem.shop')
      : `${t('general.get')} ${t('basicType.bonus')}`;

  if (isLoading) {
    return (
      <ModalWrapper
        onClose={handleClose}
        className="max-h-[90%] 1440:max-h-[800px] max-w-[340px]"
        containerClassName="flex flex-col py-[30px] px-5">
        <Skeletons className="h-[64px] w-[300px] mb-3" />
        <Skeletons className="h-[100px] w-[300px] mb-3" />
        <Skeletons className="h-[64px] w-[300px] mb-2" />
        <Skeletons className="h-10 w-[300px] mb-2" />
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper
      onClose={handleClose}
      className="max-h-[90%] 1440:max-h-[800px] max-w-[340px]"
      containerClassName="flex flex-col py-[30px] px-5">
      <p className="title-shadow-event font-bold text-[30px] leading-8 uppercase mb-3 text-center">
        {data?.name}
      </p>
      <CmsContent
        content={data?.fullDescription || ''}
        className="text-me_content text-sm font-medium [&_strong]:font-bold mb-3"
      />
      {prizeAmountToDisplay && (
        <div className="bg-me_deposit_bonus_bg flex flex-col items-center justify-center rounded-[8px] py-2.5 px-4 mb-3">
          <p className="text-me_deposit_bonus_text text-xs font-medium text-center">
            {t('eventModal.hereYourPrize')}
          </p>
          <p className="title-shadow-event font-bold text-xl text-center">
            {prizeAmountToDisplay} {t('shopEvent.eggs')}
          </p>
        </div>
      )}

      <Button
        className="min-w-full mb-3"
        mode="button-primary"
        title={buttonText}
        label={buttonText}
        onClick={handleButtonClick}
      />
    </ModalWrapper>
  );
};
